import {
  BulkProductResolutionResponse,
  EntityError,
  OrderItemDto,
  ReplenishmentErrorCodes,
  ResolvedProductMap,
  UnresolvedProductMap,
} from "@deliverr/replenishment-client";
import { FormattedMessage } from "common/components/ui/FormattedMessage";
import React from "react";
import { SPThunkAction } from "common/ReduxUtils";
import { logError } from "Logger";
import { toast } from "react-toastify";
import {
  transferCreateSetBulkProductUploadResult,
  transferCreateResetProductSelection,
} from "transfers/create/actions";
import { entries, forEach, isEmpty, map } from "lodash";
import { loadProductsAndInventoryForTransfer } from "../../../actions/loadProductsAndInventoryForTransfer";
import { extractEntityErrors } from "../../../utils/errorUtils";

export const bulkImportProducts =
  (
    bulkProductResolution: BulkProductResolutionResponse,
    onError?: (errors: EntityError[]) => void
  ): SPThunkAction<Promise<boolean>> =>
  async (dispatch) => {
    const { resolvedProducts, unresolvedProducts } = bulkProductResolution;
    try {
      // Saving to generate the csv report
      dispatch(transferCreateSetBulkProductUploadResult(bulkProductResolution));
      dispatch(transferCreateResetProductSelection());

      await processUnresolvedProducts(unresolvedProducts);

      await processResolvedProducts(resolvedProducts, dispatch);

      return true;
    } catch (error) {
      logError({ fn: "transfers.bulkProductUpload.actions.bulkImportProducts" }, error, "Error importing products");
      toast.error(
        <FormattedMessage
          id="transfers.bulkProductUpload.actions.bulkImportProducts.error"
          defaultMessage="Failed to import products"
        />
      );
      onError?.(extractEntityErrors(error));
      return false;
    }
  };

const processResolvedProducts = async (resolvedProducts: ResolvedProductMap, dispatch): Promise<void> => {
  const orderItems: OrderItemDto[] = [];

  forEach(entries(resolvedProducts), ([lineItemId, resolvedProductDetails]) => {
    // TODO - Handle when different resolved products are present from multiple warehouses
    const resolvedItem = resolvedProductDetails.resolvedItems[0];
    orderItems.push({
      lineItemId,
      dsku: resolvedItem.dsku,
      totalUnits: resolvedItem.totalUnits,
      unitsPerPack: resolvedItem.unitsPerPack,
      packOf: resolvedItem.packOf,
      lotNumber: resolvedProductDetails.productRow.lotNumber,
    });
  });

  await dispatch(loadProductsAndInventoryForTransfer(orderItems));
};

const processUnresolvedProducts = async (unresolvedProducts: UnresolvedProductMap): Promise<void> => {
  // TODO - Make it self serve - V2
  if (!isEmpty(unresolvedProducts)) {
    throw new EntityError({
      code: ReplenishmentErrorCodes.INVALID_ORDER_ITEMS,
      message: "Some rows in the uploaded file contain errors. Please review the details and fix the issues.",
      errors: mapUnresolvedProductsToErrors(unresolvedProducts),
    });
  }
};

const mapUnresolvedProductsToErrors = (unresolvedProducts: UnresolvedProductMap): EntityError[] => {
  return map(entries(unresolvedProducts), ([rowNumber, unresolvedDetails]) => {
    return new EntityError({
      code: unresolvedDetails.code,
      message: unresolvedDetails.message,
      entityId: rowNumber,
      payload: {
        row: {
          ...unresolvedDetails.productRow,
          sku: unresolvedDetails.productRow.dsku ?? unresolvedDetails.productRow.msku,
        },
      },
    });
  });
};
